<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog v-model="dialog" content-class="contacts-selection-dialog" @click:outside="cancelSelection">
    <v-card id="contacts" color="surface lighten-1">
      <v-card-title primary-title>
        {{ computedTitle }}

        <v-btn class="close-btn" text icon @click="cancelSelection">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-row align="center" class="pa-2 pb-4" no-gutters>
        <EcsSearchInput
          ref="searchBar"
          class="ecs-search-input"
          :placeholder="computedSearchVerbiage"
          @search-input-changed="handleSearch"
        />

        <div v-show="!isTelephony" class="notification">
          <v-icon small>mdi-alert-circle</v-icon>
          {{ $t('filteredContactsMessage') }}
        </div>

        <v-btn v-if="isTelephony" class="dialpad-btn" :color="dialpadButtonColor" text icon @click="toggleDialpad">
          <v-icon>mdi-dialpad</v-icon>
        </v-btn>
      </v-row>

      <v-card-text class="sections-container pa-0" :class="{ hover: showDialpad }">
        <v-expansion-panels v-model="sectionOpenStates" class="list-section" :class="{ hover: showDialpad }">
          <EcsContactsList
            :header="$t('section.favorites')"
            :items="favorites"
            :search="searchInput"
            :max-height="getListHeight(favorites)"
            contact-selection
            :use-single-selection="useSingleSelection"
            @contacts-selection="commitSelection"
          />
          <EcsContactsList
            :header="$t('section.supervisors')"
            :items="supervisors"
            :search="searchInput"
            :max-height="getListHeight(supervisors)"
            contact-selection
            :use-single-selection="useSingleSelection"
            @contacts-selection="commitSelection"
          />
          <EcsContactsList
            :header="$t('section.agents')"
            :items="agents"
            :search="searchInput"
            :max-height="getListHeight(agents)"
            contact-selection
            :use-single-selection="useSingleSelection"
            @contacts-selection="commitSelection"
          />
          <EcsContactsList
            :header="$t('section.channels')"
            :items="channels"
            :search="searchInput"
            :max-height="getListHeight(channels)"
            contact-selection
            :use-single-selection="useSingleSelection"
            @contacts-selection="commitSelection"
          />
          <EcsContactsList
            :header="$t('section.businessProcesses')"
            :items="businessProcesses"
            :search="searchInput"
            :max-height="getListHeight(businessProcesses)"
            contact-selection
            :use-single-selection="useSingleSelection"
            @contacts-selection="commitSelection"
          />
          <EcsContactsList
            v-if="isTelephony"
            :header="$t('section.enterprises')"
            :items="enterpriseContacts"
            :search="searchInput"
            :loading="loadingCompanyContacts"
            :max-height="getListHeight(enterpriseContacts)"
            contact-selection
            :use-single-selection="useSingleSelection"
            @contacts-selection="commitSelection"
          />
          <EcsContactsList
            v-if="isTelephony"
            ref="crmContactsContainer"
            :header="$t('section.crmContacts')"
            :items="crmContacts"
            :search="searchInput"
            :max-height="getListHeight(crmContacts)"
            contact-selection
            :use-single-selection="useSingleSelection"
            @contacts-selection="commitSelection"
            @load-more-data="onLoadingCrmContacts"
          />
          <EcsContactsList
            v-if="isTelephony"
            ref="externalAddresBook"
            :header="$t('section.externalAddressBook')"
            :items="externalContactsToDisplay"
            :search="searchInput"
            :loading="loadingExternalContacts"
            :max-height="getListHeight(externalContacts)"
            contact-selection
            :use-single-selection="useSingleSelection"
            @contacts-selection="commitSelection"
          />
        </v-expansion-panels>

        <EcsDialpad
          :show="showDialpad"
          :disabled="isOfflineModeEnabled"
          hide-bp
          hide-fab
          show-send-button
          centered
          embedded
          @dial="handleDial"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import EcsDialpad from '@/components/ecs-dialpad/EcsDialpad';
import { MEDIA_TYPE_TELEPHONY } from '@/constants/socket-events';
import { filterSupervisorsFromAgents, filterAgentById } from '@/utils';
import EcsContactsList from '@/components/ecs-contacts-directory/EcsContactsList';
import ContactService from '@/services/contactService';
import EcsCrmContact from '@/classes/contacts/EcsCrmContact';
import { CONTACT_TYPE } from '@/constants/contactConstants';

const CONTACTS_SELECTION_EVENT = 'contacts-selection';
const CONTACTS_SELECTION_CANCELLED_EVENT = 'contacts-selection-cancelled';

export default {
  name: 'EcsContactsSelectionDialog',

  components: { EcsContactsList, EcsDialpad },

  props: {
    // Boolean to Enable single selection for the Contact selection dialog lists.
    useSingleSelection: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      availableContactsHeight: 0,
      availableTotalHeight: 0,
      dialog: false,
      searchInput: '',
      searchVerbiage: '',
      sectionOpenStates: 0,
      showDialpad: false,
      title: '',
      mediaType: '',
      crm: {
        data: [],
        nextNumberOfMatches: 25
      },
      loadingCompanyContacts: false,
      loadingExternalContacts: false
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('CONTACTS', [
      'getBusinessProcesses',
      'getChannels',
      'getEnterpriseContacts',
      'getAgents',
      'getExternalContacts',
      'getCrmContacts',
      'getFavoriteContacts',
      'getSupervisors',
      'getAllContacts',
      'getContactV2'
    ]),

    ...mapGetters('INTERACTIONS', ['getCrmContactsForInteractions']),

    ...mapGetters('SETTINGS', ['isOfflineModeEnabled']),

    computedTitle() {
      return this.title || this.$t('title');
    },

    computedSearchVerbiage() {
      return this.searchVerbiage || this.$t('searchPlaceholder');
    },

    favorites() {
      let filteredFavorites = [];

      if (this.getFavoriteContacts.favorites && this.getFavoriteContacts.favorites.length) {
        filteredFavorites = this.getFavoriteContacts.favorites
          .map(({ id }) => this.getContactV2(id))
          .filter(contact => contact);
      } else {
        filteredFavorites = ([...this.getFavoriteContacts.internal, ...this.getFavoriteContacts.external] || [])
          .map(favoriteId => this.getContactV2(favoriteId))
          .filter(contact => contact);
      }

      // Agents, Supervisors, Business Processes, Channels and Enterprise Contacts are available for all interaction types
      // Only if "isTelephony" add CrmContacts and ExternalContacts should be added
      return this.filterContacts(filteredFavorites, this.searchInput).filter(contact => {
        return this.isTelephony
          ? true
          : ![CONTACT_TYPE.crm, CONTACT_TYPE.external, CONTACT_TYPE.enterprise].includes(contact.contactType);
      });
    },

    agents() {
      let filteredAgents = filterSupervisorsFromAgents(this.getAgents, this.getSupervisors);
      if (this.loggedInAgent) {
        filteredAgents = filterAgentById(filteredAgents, this.loggedInAgent.id);
      }

      return filteredAgents.filter(agent => this.includesSearchValue(agent, ['name', 'endPoint']));
    },

    supervisors() {
      return this.getSupervisors.filter(supervisor => this.includesSearchValue(supervisor, ['name', 'endPoint']));
    },

    businessProcesses() {
      return this.getBusinessProcesses.filter(bp => this.includesSearchValue(bp, ['name', 'endPoint']));
    },

    channels() {
      return this.getChannels.filter(channel => this.includesSearchValue(channel, ['name', 'endPoint']));
    },

    enterpriseContacts() {
      return this.getEnterpriseContacts.filter(contact =>
        this.includesSearchValue(contact, [
          'name',
          'endPoint',
          'HPBX Extension',
          'HPBX User',
          'Primary Email',
          'broadsoftIdentifer'
        ])
      );
    },

    crmContacts() {
      const filteredRecentUsedContacts = this.getCrmContactsForInteractions.filter(this.includesSearchValue) || [];
      const filteredAvailableContacts = this.crm.data || [];

      return [...filteredRecentUsedContacts, ...filteredAvailableContacts];
    },

    externalContacts() {
      return this.getExternalContacts;
    },

    externalContactsToDisplay() {
      return this.externalContacts.filter(contact => {
        return new RegExp(this.searchInput, 'i').test(JSON.stringify(contact));
      });
    },

    isTelephony() {
      return this.mediaType.toUpperCase() === MEDIA_TYPE_TELEPHONY;
    },

    dialpadButtonColor() {
      return this.showDialpad ? 'primary' : '';
    }
  },

  watch: {
    dialog() {
      if (this.dialog) {
        this.$root.$emit('request-contacts');
      }
    },
    availableTotalHeight(height) {
      const dialogMarginFromWindow = 30;
      this.availableContactsHeight = height - dialogMarginFromWindow;
    }
  },

  created() {
    window.addEventListener('resize', this.calculateHeight);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.calculateHeight);
  },

  methods: {
    ...mapActions('CONTACTS', [
      'requestCompanyContacts',
      'requestExternalContacts',
      'requestCrmContactBatch',
      'requestFavoriteContacts'
    ]),

    /**
     * Function to evaluate if a contact matches with the search criteria.
     * @param contact The contact to be evaluated.
     * @param fields All the fields to be evaluated.
     * @return includes - The boolean that indicates if the contact matches with the search criteria.
     */
    includesSearchValue(contact, fields) {
      if (!contact) {
        return false;
      }

      if (!fields || !Array.isArray(fields)) {
        fields = ['name'];
      }

      return fields.some(field => new RegExp(this.searchInput, 'i').test(contact[field]));
    },

    /**
     * Calculates the ideal height for the list content based on the available space
     * @param list {Array} - The list that will be calculated the available space
     * @return height - The ideal height for the list content.
     */
    getListHeight(list = []) {
      const minHeight = 144;
      const listItemHeight = 60;
      const totalListHeight = list.length * listItemHeight;
      const computedHeight =
        totalListHeight < this.availableContactsHeight ? totalListHeight : this.availableContactsHeight;
      return computedHeight < minHeight ? minHeight : computedHeight;
    },

    cancelSelection() {
      if (this.$refs.crmContactsContainer) {
        this.$refs.crmContactsContainer.restartScroll();
      }
      this.dialog = false;
      this.showDialpad = false;
      this.sectionOpenStates = 0;
      this.clearSelection();
      this.$off(CONTACTS_SELECTION_EVENT);
      this.$emit(CONTACTS_SELECTION_CANCELLED_EVENT, 'USER_ACTION:CANCEL');
    },

    clearSelection() {
      this.$refs.searchBar.clear();
    },

    commitSelection(selection) {
      this.$emit(CONTACTS_SELECTION_EVENT, selection);
      this.$off(CONTACTS_SELECTION_CANCELLED_EVENT);
      this.$refs.searchBar.clear();
      if (this.$refs.crmContactsContainer) {
        this.$refs.crmContactsContainer.restartScroll();
      }
      this.dialog = false;
    },

    handleDial({ businessProcessId, phoneNumber }) {
      this.showDialpad = false;
      this.commitSelection({ endPoint: phoneNumber });
    },

    /**
     * This method will set the input search value into a variable that will be used for filter criterias.
     * Also emmit the CRM trigger to retrieve contact based on the search value.
     * @param searchValue The search input value.
     */
    handleSearch(searchValue) {
      this.searchInput = searchValue;
      this.searchCrmContacts(searchValue);
    },

    open() {
      this.dialog = true;
      this.calculateHeight();
      this.searchCrmContacts('');
    },

    /**
     * It calculates the available height for list contents based on the dialog basic content and the window inner
     * Each section header has 48px, the dialog title has 54 px, and the search area has 60px.
     * The amount of list is dynamic so will be calculated based on the list to render.
     */
    calculateHeight() {
      const numberOfListToRender = this.isTelephony ? 9 : 5;
      const listHeadersHeight = numberOfListToRender * 48;
      const dialogHeader = 172;

      this.availableTotalHeight = document.body.clientHeight - listHeadersHeight - dialogHeader;
    },

    setDialogTitle(customTitle) {
      this.title = customTitle || this.$t('title');
      return this;
    },

    setDialogSearchVerbiage(customSearchVerbiage) {
      this.searchVerbiage = customSearchVerbiage || this.$t('searchPlaceholder');
      return this;
    },

    setMediaType(mediaType) {
      this.mediaType = mediaType;
      return this;
    },

    filterContacts(listToFilter, textToFilter) {
      if (!listToFilter) {
        return [];
      }

      let filteredList = listToFilter;

      if (textToFilter && textToFilter.trim() !== '') {
        filteredList = filteredList.filter(contact => {
          return (
            this.verifyByField(contact, 'name', textToFilter) ||
            this.verifyByField(contact, 'firstName', textToFilter) ||
            this.verifyByField(contact, 'lastName', textToFilter) ||
            this.verifyByField(contact, 'endPoint', textToFilter) ||
            this.verifyByField(contact, 'phones', textToFilter) ||
            this.verifyByField(contact, 'email', textToFilter) ||
            this.verifyByField(contact, 'emails', textToFilter) ||
            this.verifyByField(contact, 'HPBX Extension', textToFilter) ||
            this.verifyByField(contact, 'Mobile Phone', textToFilter) ||
            this.verifyByField(contact, 'Primary Email', textToFilter) ||
            this.verifyByField(contact, 'name', textToFilter) ||
            this.verifyByField(contact, 'endPoint', textToFilter) ||
            this.verifyByField(contact, 'searchString', textToFilter)
          );
        });
      }

      return filteredList;
    },

    verifyByField(contact, field, searchCriteria) {
      let containsCriteria = false;

      if (Array.isArray(contact[field])) {
        contact[field].forEach(objectValue => {
          if (typeof objectValue.value === 'string' && objectValue.value.toLowerCase().includes(searchCriteria)) {
            containsCriteria = true;
          }
        });
      } else {
        containsCriteria =
          typeof contact[field] === 'string' && contact[field].toLowerCase().includes(searchCriteria.toLowerCase());
      }

      return containsCriteria;
    },

    select() {
      this.open();
      return new Promise((resolve, reject) => {
        this.$once(CONTACTS_SELECTION_EVENT, resolve);
        this.$once(CONTACTS_SELECTION_CANCELLED_EVENT, reject);
      });
    },

    toggleDialpad() {
      this.showDialpad = !this.showDialpad;
    },

    searchCrmContacts(searchText, page = 1) {
      if (page === 1) {
        this.crm = {
          data: [],
          nextNumberOfMatches: 25
        };
      }

      let service;
      if (searchText) {
        service = ContactService.basicSearchCrmContact(searchText, this.crm.nextNumberOfMatches, page);
      } else {
        service = ContactService.advancedSearchCrmContact({}, this.crm.nextNumberOfMatches, page);
      }

      service
        .then(response => {
          const newContacts = response.data.contacts.map(contact => new EcsCrmContact(contact));
          const contacts = page === 1 ? newContacts : [...this.crm.data, ...newContacts];
          this.crm = {
            data: contacts,
            nextNumberOfMatches: response.data.totalNumberOfMatches > contacts.length ? this.crm.nextNumberOfMatches : 0
          };
        })
        .catch(() => {
          this.crm.data = [...this.crm.data];
        });
    },

    onLoadingCrmContacts() {
      const nextPage = Math.ceil(this.crm.data.length / this.crm.nextNumberOfMatches) + 1;
      this.searchCrmContacts(this.contactsSearchText, nextPage);
    }
  }
};
</script>

<style>
.contacts-selection-dialog {
  width: 500px;
  border-radius: 10px !important;
  max-height: 100% !important;
}

.contacts-selection-dialog .close-btn {
  position: absolute;
  right: 8px;
}

.contacts-selection-dialog .ecs-search-input {
  margin: 0 8px !important;
  height: 28px;
  flex: 1;
}

.contacts-selection-dialog .sections-container {
  position: relative;
  border-top: 1px solid var(--v-border-color);
  overflow-y: auto;
  max-height: calc(100vh - 200px);
}

.contacts-selection-dialog .sections-container.hover {
  min-height: 350px;
}

.contacts-selection-dialog .list-section.hover {
  display: none;
}

.contacts-selection-dialog .notification .v-icon {
  margin-top: -3px;
}

.contacts-selection-dialog .notification {
  font-size: 12px;
  width: 100%;
  padding: 16px 0 0 8px;
  color: var(--v-text-base);
  opacity: 0.5;
}
</style>
